import {
  showErrorMessageBarAction,
  showModalAction,
  ModalName,
  showCreateLoginModalDispatcher,
} from "../redux/action/UIActions";
import {
  isAccountAdmin,
  logoutDispatcher,
  updateSessionKeyAction,
} from "../redux/action/LoginActions";
import {
  currentEntityAction,
  EntityType,
} from "../redux/action/CurrentActions";
import {
  getAuthorizationHeaderValue,
  getIdentityHeaderValue,
  redirectToGoogleSignIn,
} from "./AmplifyLoginService";
import { datadogLogs } from "@datadog/browser-logs";
import { isSSR } from "../util/Utils";
import { serverLogger } from "./ServerLogger";

export const getBaseUrl = (getState) => {
  const baseUrl = getState && getState() && getState().login.baseUrl;
  return !!baseUrl ? baseUrl : "";
};

export const getAuthHeaders = async (getState) => {
  //Don't set null values because NextJs will send as strings:
  const autHeaders = {};
  const authorization = await getAuthorizationHeaderValue();
  if (authorization) autHeaders["Authorization"] = authorization;
  if (getState && getState().login.sessionKey)
    autHeaders["x-session-key"] = getState().login.sessionKey;
  return autHeaders;
};

export const getIdentityHeader = async (getState) => {
  const idHeader = await getIdentityHeaderValue();
  return {
    Identity: idHeader,
  };
};

export const processServiceResponse = (
  dispatch,
  getState,
  error,
  response,
  resolve,
  reject
) => {
  // This method is also hotwired by SSR getContentAndChannel:
  const userSession = getState && getState().current?.entity?.userSession;
  if (typeof response === "undefined") {
    dispatch &&
      dispatch(
        showErrorMessageBarAction(true, "Not connected to the Internet")
      );
    reject({
      errorCode: "NetworkError",
      errorMessage: error,
      userMessage: "Not connected to the Internet",
    });
  } else {
    if (response.ok) return resolve(response.body);
    else {
      if (!response.statusCode || response.notFound) {
        dispatch &&
          dispatch(
            showErrorMessageBarAction(true, "Server error. Contact support")
          );
        reject({ errorCode: "ServerError", errorMessage: response.statusText });
      } else if (response.statusCode === 502) {
        dispatch &&
          dispatch(
            showErrorMessageBarAction(true, "Server offline. Contact support")
          );
        reject({ errorCode: "ServerError", errorMessage: response.statusText });
      } else {
        if (
          typeof response.body !== "undefined" &&
          response.body &&
          typeof response.body.errorCode !== "undefined"
        ) {
          if (
            response.statusCode === 401 &&
            response.body.errorCode === "NotSignedIn"
          ) {
            dispatch && dispatch(updateSessionKeyAction(null));
            dispatch && dispatch(showModalAction(ModalName.LoginModal, true));
          } else if (
            response.statusCode === 401 &&
            response.body.errorCode === "LoginRequired"
          ) {
            dispatch && dispatch(updateSessionKeyAction(null));
            dispatch && dispatch(showCreateLoginModalDispatcher());
          } else if (
            response.statusCode === 403 &&
            response.body.errorCode === "GoogleSignInRequired"
          ) {
            dispatch && dispatch(updateSessionKeyAction(null));
            redirectToGoogleSignIn();
          } else if (
            //TBD: Implement SessionExpired Logic server side:
            response.statusCode === 401 &&
            response.body.errorCode === "SessionExpired"
          ) {
            dispatch && dispatch(logoutDispatcher());
            dispatch && dispatch(showModalAction(ModalName.LoginModal, true));
          } else if (
            response.statusCode === 410 &&
            response.body.errorCode === "ShareExpired"
          ) {
            dispatch &&
              dispatch(
                showModalAction(
                  ModalName.RequestShareModal,
                  true,
                  "requestShare"
                )
              );
          } else if (
            response.statusCode === 417 &&
            response.body.errorCode === "EmailVerificationRequired"
          ) {
            dispatch &&
              dispatch(
                currentEntityAction(EntityType.User, {
                  emailAddress: response.body.emailAddress,
                })
              );
            dispatch &&
              dispatch(showModalAction(ModalName.VerifyEmailPinModal, true));
          } else if (
            response.statusCode === 417 &&
            response.body.errorCode === "PhoneVerificationRequired"
          ) {
            window.router.navigate("/");
            dispatch &&
              dispatch(currentEntityAction(EntityType.User, response.body));
            dispatch &&
              dispatch(showModalAction(ModalName.VerifyMobileModal, true));
          } else if (
            response.statusCode === 403 &&
            response.body.errorCode === "AccountSignInRequired"
          ) {
            //Give some time for the user to see the error message:
            setTimeout(() => {
              if (window) window.location = response.body.redirectUrl;
            }, 3000);
          } else if (
            response.statusCode === 417 &&
            response.body.errorCode === "PhoneRequired"
          ) {
            window.router.navigate("/myProfile");
          }
          dispatch &&
            response.body.userMessage &&
            dispatch(
              showErrorMessageBarAction(true, response.body.userMessage)
            );
          logWarning("processServiceResponse", response.body, error);
          reject(response.body);
        } else if (
          response.statusCode === 410 &&
          response.body.errorCode === "UpgradeRequired"
        ) {
          dispatch &&
            dispatch(
              showErrorMessageBarAction(true, response.body.userMessage)
            );
          if (
            userSession &&
            isAccountAdmin(userSession.accountUser, userSession.account)
          )
            window.router.navigate("/account/billing");
        } else {
          dispatch &&
            response.statusCode < 500 &&
            dispatch(
              showErrorMessageBarAction(
                true,
                response.statusText || response.error?.message
              )
            );
          reject({
            errorCode: "ServerError",
            errorMessage: response.statusText || response.error?.message,
          });
          logError(
            "processServiceResponse",
            {
              responseErrorMessage: response.error?.message,
              responseBody: response.body,
            },
            error
          );
        }
      }
    }
  }
};

export const logDebug = (message, messageContext = {}) => {
  if (!isSSR) {
    console.log("logDebug: " + message, messageContext);
    datadogLogs.logger.debug(message, messageContext);
  } else serverLogger.debug(message, messageContext);
};

export const logInfo = (message, messageContext = {}) => {
  if (!isSSR) {
    console.log("logInfo: " + message, messageContext);
    datadogLogs.logger.info(message, messageContext);
  } else serverLogger.info(message, messageContext);
};

export const logWarning = (message, messageContext = {}, error) => {
  if (!isSSR) {
    console.warn("logWarning: " + message, messageContext, error);
    datadogLogs.logger.warn(message, messageContext, error);
  } else serverLogger.warn(message, messageContext, error);
};

const normalizeErrorLogArgs = (message, messageContext = {}, error) => {
  const getErrorMessage = (errorValue) => {
    if (typeof errorValue === "string") return errorValue;
    if (errorValue?.message) return errorValue.message;
    if (errorValue?.errorMessage) return errorValue.errorMessage;
    try {
      return JSON.stringify(errorValue);
    } catch {
      return String(errorValue);
    }
  };

  const getLogMessage = (messageValue) => {
    if (typeof messageValue === "string") return messageValue;
    return getErrorMessage(messageValue) || "Application error";
  };

  const getMessageContext = (contextValue) => {
    if (contextValue === undefined || contextValue === null) return {};
    if (contextValue instanceof Error) {
      return {
        errorName: contextValue.name,
        errorMessage: contextValue.message,
      };
    }
    return typeof contextValue === "object"
      ? contextValue
      : { context: contextValue };
  };

  if (message instanceof Error) {
    return {
      message: message.message || "Error",
      messageContext: getMessageContext(messageContext),
      error: message,
    };
  }

  if (messageContext instanceof Error && error === undefined) {
    return {
      message: getLogMessage(message),
      messageContext: {},
      error: messageContext,
    };
  }

  if (error instanceof Error) {
    return {
      message: getLogMessage(message),
      messageContext: getMessageContext(messageContext),
      error,
    };
  }

  if (error !== undefined && error !== null) {
    const normalizedError = new Error(getErrorMessage(error));
    return {
      message: getLogMessage(message),
      messageContext: getMessageContext(messageContext),
      error: normalizedError,
    };
  }

  return {
    message: getLogMessage(message),
    messageContext: getMessageContext(messageContext),
    error: new Error(getLogMessage(message)),
  };
};

export const logError = (message, messageContext = {}, error) => {
  const normalizedLog = normalizeErrorLogArgs(message, messageContext, error);
  if (!isSSR) {
    console.log(
      "logError: " + normalizedLog.message,
      normalizedLog.messageContext,
      normalizedLog.error
    );
    datadogLogs.logger.error(
      normalizedLog.message,
      normalizedLog.messageContext,
      normalizedLog.error
    );
  } else {
    serverLogger.error(
      normalizedLog.message,
      normalizedLog.messageContext,
      normalizedLog.error
    );
  }
};
